import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { TimePeriod } from './driver-score-utils';

/**
 * Props for the DriverScoreEmptyState component.
 */
interface DriverScoreEmptyStateProps {
	/** The currently selected time period for contextual messaging. */
	timePeriod: TimePeriod;
}

/**
 * Returns the appropriate period label for the empty state message.
 *
 * @param timePeriod - The selected time period
 * @returns Human-readable period label
 */
const getPeriodLabel = (timePeriod: TimePeriod): string => {
	switch (timePeriod) {
		case 'week':
			return 'this week';
		case 'month':
			return 'this month';
		default:
			return 'yet';
	}
};

/**
 * Empty state component displayed when there are no trips recorded.
 *
 * Shows a friendly message with a car icon when the driver hasn't
 * made any trips in the selected time period. The message adjusts
 * based on whether viewing overall, weekly, or monthly scores.
 *
 * @component
 * @example
 * ```tsx
 * <DriverScoreEmptyState timePeriod="week" />
 * // Shows: "No trips recorded this week"
 *
 * <DriverScoreEmptyState timePeriod="overall" />
 * // Shows: "No trips recorded yet"
 * ```
 */
const DriverScoreEmptyState: FC<DriverScoreEmptyStateProps> = ({ timePeriod }) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				py: 2,
				mb: 3,
				backgroundColor: theme.palette.grey[50],
				borderRadius: 2,
				border: `1px dashed ${theme.palette.grey[300]}`,
			}}
		>
			<DirectionsCarIcon
				sx={{
					fontSize: 50,
					color: theme.palette.grey[400],
					mb: 1.5,
				}}
			/>
			<Typography
				variant='body1'
				sx={{
					fontWeight: 600,
					color: theme.palette.text.secondary,
					mb: 0.5,
				}}
			>
				No trips recorded {getPeriodLabel(timePeriod)}
			</Typography>
			<Typography
				variant='body2'
				sx={{
					color: theme.palette.text.disabled,
					textAlign: 'center',
					px: 2,
				}}
			>
				Start driving to see your score!
			</Typography>
		</Box>
	);
};

export default DriverScoreEmptyState;
